import { useLang } from "@/context/LanguageContext";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" },
  }),
};

const MARKET_IMAGES = [
  {
    label: "Utilities & Generators",
    url: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?auto=format&fit=crop&w=900&q=80",
    visual: "GRID",
  },
  {
    label: "Mining & Industrial Clients",
    url: "https://images.pexels.com/photos/461593/pexels-photo-461593.jpeg?auto=compress&cs=tinysrgb&w=900",
    visual: "MINING",
  },
  {
    label: "Telecom & Critical Infrastructure",
    url: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=900&q=80",
    visual: "TELCO",
  },
  {
    label: "Data Centers & AI Infrastructure",
    url: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=900&q=80",
    visual: "DATA",
  },
  {
    label: "Investors & Developers",
    url: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=900&q=80",
    visual: "CAPITAL",
  },
  {
    label: "International OEMs",
    url: "https://images.unsplash.com/photo-1581090700227-1e37b190418e?auto=format&fit=crop&w=900&q=80",
    visual: "OEM",
  },
  {
    label: "IPPs & Renewable Developers",
    url: "https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=900&q=80",
    visual: "SOLAR",
  },
  {
    label: "Strategic Partners",
    url: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=900&q=80",
    visual: "PARTNERS",
  },
];

function MarketFallback({ label, visual }) {
  return (
    <div className="absolute inset-0 bg-[#0A192F] overflow-hidden">
      <div className="absolute inset-0 opacity-25 bg-[radial-gradient(circle_at_20%_20%,#0D9488_0,transparent_30%),radial-gradient(circle_at_80%_70%,#38BDF8_0,transparent_28%)]" />
      <svg viewBox="0 0 900 360" className="absolute inset-0 w-full h-full opacity-80" aria-hidden="true">
        <g fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
          {visual === "MINING" && (
            <>
              <path d="M80 260h720" />
              <path d="M105 260l85-80 130 80 120-115 160 115 115-70 95 70" />
              <rect x="250" y="210" width="185" height="45" rx="5" />
              <path d="M300 210l35-45h85l35 45" />
              <circle cx="295" cy="263" r="22" />
              <circle cx="395" cy="263" r="22" />
              <path d="M465 235h150l45 28h-195z" />
              <circle cx="505" cy="265" r="18" />
              <circle cx="610" cy="265" r="18" />
            </>
          )}
          {visual === "GRID" && (
            <>
              <path d="M160 285L245 80l85 205M198 190h94M180 240h130" />
              <path d="M570 285L655 80l85 205M608 190h94M590 240h130" />
              <path d="M245 110c120 60 290 60 410 0" />
              <path d="M245 180c120 45 290 45 410 0" />
            </>
          )}
          {visual === "TELCO" && (
            <>
              <path d="M450 285V95" />
              <path d="M370 285l80-190 80 190" />
              <path d="M405 165h90M390 220h120" />
              <path d="M310 135c-45 35-65 80-65 130M590 135c45 35 65 80 65 130" />
              <path d="M270 90c-75 55-110 115-110 190M630 90c75 55 110 115 110 190" />
            </>
          )}
          {visual === "DATA" && (
            <>
              <rect x="210" y="85" width="150" height="205" rx="10" />
              <rect x="390" y="85" width="150" height="205" rx="10" />
              <rect x="570" y="85" width="120" height="205" rx="10" />
              <path d="M240 130h90M240 175h90M240 220h90M420 130h90M420 175h90M420 220h90M600 130h60M600 175h60M600 220h60" />
            </>
          )}
          {visual === "SOLAR" && (
            <>
              <circle cx="170" cy="95" r="42" />
              <path d="M120 285h650" />
              <path d="M250 160h230l-55 95H195zM520 160h230l-55 95H465z" />
              <path d="M250 205h200M520 205h200M330 160l-35 95M610 160l-35 95" />
            </>
          )}
          {(visual === "CAPITAL" || visual === "PARTNERS" || visual === "OEM") && (
            <>
              <rect x="170" y="195" width="140" height="90" rx="8" />
              <rect x="380" y="130" width="140" height="155" rx="8" />
              <rect x="590" y="75" width="140" height="210" rx="8" />
              <path d="M240 195v-55h210v-10M520 130V95h140" />
              <circle cx="240" cy="120" r="22" />
              <circle cx="450" cy="95" r="22" />
              <circle cx="660" cy="55" r="22" />
            </>
          )}
        </g>
      </svg>
      <div className="absolute left-6 bottom-6 right-6">
        <p className="text-xs uppercase tracking-[0.22em] font-bold text-[#5EEAD4] mb-2">GJC Market</p>
        <p className="text-xl font-medium text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>{label}</p>
      </div>
    </div>
  );
}

export default function Markets() {
  const { t } = useLang();

  return (
    <div data-testid="markets-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#0D9488] mb-4"
          >
            {t.markets.overline}
          </motion.p>
          <motion.h1
            initial="hidden"
            animate="visible"
            custom={1}
            variants={fadeUp}
            className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#0A192F] max-w-4xl mb-6"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t.markets.headline}
          </motion.h1>
          <motion.p
            initial="hidden"
            animate="visible"
            custom={2}
            variants={fadeUp}
            className="text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl"
          >
            {t.markets.intro}
          </motion.p>
        </div>
      </section>

      {/* Market Sectors Grid */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="markets-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {t.markets.sectors.map((sector, i) => {
              const marketImage = MARKET_IMAGES[i];

              return (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={i}
                  variants={fadeUp}
                  className="bg-white border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1"
                  data-testid={`market-sector-${i}`}
                >
                  {marketImage && (
                    <div className="h-56 overflow-hidden bg-[#0A192F] relative">
                      <MarketFallback label={sector.title} visual={marketImage.visual} />
                      <img
                        src={marketImage.url}
                        alt={`${sector.title} — ${marketImage.label}`}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                      <div className="absolute inset-0 bg-[#0A192F]/20" />
                    </div>
                  )}
                  <div className="p-8">
                    <h3
                      className="text-lg font-medium text-[#0A192F] mb-3"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      {sector.title}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{sector.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0A192F]" data-testid="markets-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-3xl md:text-4xl font-medium tracking-tight text-white mb-6"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t.contact.headline}
          </motion.h2>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
            variants={fadeUp}
          >
            <Link
              to="/contact"
              data-testid="markets-contact-cta"
              className="inline-flex items-center bg-[#0D9488] text-white px-7 py-3.5 text-sm font-medium hover:bg-[#0F766E] transition-colors duration-300"
            >
              {t.nav.talkToUs} <ArrowRight size={18} className="ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
